<template>
  <!-- Table Container Card -->
  <b-card
    no-body
  >

    <div class="m-2">

      <!-- Table Top -->
      <b-row>

        <!-- Per Page -->
        <b-col
          cols="12"
          md="6"
          class="d-flex align-items-center justify-content-start mb-1 mb-md-0"
        >
          <TableEntries
            v-model="perPage"
            :options="perPageOptions"
          />
          <Button
            v-if="getUserPermission('CREATE_BUSINESS_AUTOMATION')"
            :label="$t('Add')"
            size="md"
            @click="showModal('modal-lg')"
          />
        </b-col>

        <!-- Search -->
        <b-col
          cols="12"
          md="6"
        >
          <div class="d-flex align-items-center justify-content-lg-end">
            <Button
              :label="$t('Filter')"
              size="md"
              icon="FilterIcon"
              show-icon
              class="mr-1"
              @click="showModal('modal-filter')"
            />
            <Button
              :label="$t('Reset Filter')"
              size="md"
              icon="RotateCwIcon"
              show-icon
              @click="resetFilter"
            />
          </div>
        </b-col>
      </b-row>

    </div>

    <b-table
      ref="shortagesAccountRef"
      :busy="isBusy"
      :items="overageAccountList"
      responsive
      :fields="translatedTableHeaders"
      primary-key="id"
      show-empty
      :empty-text="$t('No records available')"
      :sort-by.sync="sortBy"
      :sort-desc.sync="sortDesc"
      class="position-relative"
    >

      <template #table-busy>
        <div class="text-center text-danger my-2">
          <b-spinner
            class="align-middle"
            variant="primary"
          />
        </div>
      </template>
      <template #cell(SN)="data">
        {{ getOverallIndex(data.index) }}
      </template>

      <!-- Column: Actions -->
      <template #cell(actions)="data">

        <div class="text-nowrap">
          <!-- Dropdown -->
          <b-dropdown
            variant="link"
            toggle-class="p-0"
            no-caret
            :right="$store.state.appConfig.isRTL"
          >
            <template #button-content>
              <feather-icon
                icon="MoreVerticalIcon"
                size="16"
                class="align-middle text-body"
              />
            </template>
            <b-dropdown-item
              v-if="getUserPermission('MODIFY_BUSINESS_AUTOMATION')"
              @click="onSelectOverageAccount(data.item, $event.target, 'edit')"
            >
              <feather-icon icon="EditIcon" />
              <span class="align-middle ml-50"> {{ $t('Modify') }} </span>
            </b-dropdown-item>

            <b-dropdown-item @click="onSelectOverageAccount(data.item, $event.target, 'view')">
              <feather-icon icon="EyeIcon" />
              <span class="align-middle ml-50"> {{ $t('View') }} </span>
            </b-dropdown-item>

            <b-dropdown-item @click="onDeleteOverageAccount(data.item, $event.target)">
              <feather-icon icon="TrashIcon" />
              <span class="align-middle ml-50"> {{ $t('Delete') }} </span>
            </b-dropdown-item>
          </b-dropdown>
        </div>
      </template>

    </b-table>

    <!-- Pagination -->
    <TablePagination
      v-model="currentPage"
      :from="from"
      :to="to"
      :total-rows="totalEntries"
      :per-page="perPage"
    />

    <!-- modal -->
    <b-modal
      id="modal-lg"
      ref="my-modal"
      no-close-on-backdrop
      :ok-title="buttonLabel"
      :cancel-title="$t('Cancel')"
      cancel-variant="outline-secondary"
      centered
      :ok-disabled="isLoading"
      size="md"
      :title="modalTitle"
      @hidden="resetModal"
      @ok="handleSubmit"
    >
      <validation-observer ref="overageAccountRules">
        <b-form @submit.prevent="handleSubmit">
          <b-row class="match-height">
            <!-- Branch -->
            <b-col md="12">
              <SelectField
                id="branch"
                v-model="overageAccountFormData.branchCode"
                :label="$t('Branch')"
                :options="branchOptions"
                :reduce="option => option.value"
                :error-name="$t('Branch')"
                rules="required"
                item="label"
                :placeholder="$t('Select')"
                :readonly="isReadonly"
              />
            </b-col>
            <!-- Branch -->

            <!-- Account -->
            <b-col md="12">
              <SelectField
                id="account"
                v-model="overageAccountFormData.accountNumber"
                :label="$t('Account')"
                :options="accountTypeOptions"
                :reduce="option => option.value"
                :error-name="$t('Account')"
                rules="required"
                item="label"
                :placeholder="$t('Select')"
                :readonly="isReadonly"
              />
            </b-col>
            <!-- Account -->
          </b-row>

        </b-form>
      </validation-observer>
      <template
        v-if="isReadonly"
        #modal-footer="{ }"
      >
        <div />
      </template>
    </b-modal>
    <!-- modal -->

    <!-- Filter modal -->
    <b-modal
      id="modal-filter"
      ref="my-modal"
      :ok-title="$t('Apply')"
      no-close-on-backdrop
      :cancel-title="$t('Cancel')"
      cancel-variant="outline-secondary"
      centered
      size="md"
      :title="$t('Filters')"
      modal-footer
      @ok="fetchOverageAccounts"
      @cancel="resetFilters"
    >
      <b-row class="match-height">
        <!-- Branch -->
        <b-col md="12">
          <SelectField
            id="branch"
            v-model="filter.branchID"
            :label="$t('Branch')"
            :options="branchOptions"
            :reduce="option => option.value"
            item="label"
            :placeholder="$t('Select')"
            :readonly="isReadonly"
          />
        </b-col>
        <!-- Branch -->

        <!-- Account -->
        <b-col md="12">
          <SelectField
            id="account"
            v-model="filter.accountNumber"
            :label="$t('Account')"
            :options="accountTypeOptions"
            :reduce="option => option.value"
            item="label"
            :placeholder="$t('Select')"
            :readonly="isReadonly"
          />
        </b-col>
        <!-- Account -->

      </b-row>
    </b-modal>
    <!-- Filter modal -->

    <FullScreenSpinner :show="showSpinner" />
  </b-card>

</template>

<script>
import {
  BCard, BRow, BCol, BTable, BModal, BSpinner,
  BForm, BDropdown, BDropdownItem,
} from 'bootstrap-vue'
import { ValidationObserver } from 'vee-validate'
import { mapActions, mapGetters } from 'vuex'
import { required } from '@validations'
import { modalMixins } from '@core/mixins/modal'
import { tableMixins } from '@core/mixins/table'
import { sweetAlert } from '@core/mixins/sweetAlert'
import { permissionMixin } from '@core/mixins/permission'
import { toAmount } from '@/helpers/formatAmount'
import TablePagination from '@/views/components/table/TablePagination.vue'
import TableEntries from '@/views/components/table/TableEntries.vue'
import FullScreenSpinner from '@/views/components/spinner/FullScreenSpinner.vue'
import Button from '../../../components/button/Button.vue'
import SelectField from '../../../forms/SelectField.vue'

export default {
  components: {
    BCard,
    BRow,
    BCol,
    TablePagination,
    TableEntries,
    Button,
    BTable,
    BSpinner,
    BModal,
    BForm,
    BDropdown,
    BDropdownItem,
    ValidationObserver,
    SelectField,
    FullScreenSpinner,
  },
  mixins: [modalMixins, tableMixins, sweetAlert, permissionMixin],
  data() {
    return {
      sortBy: 'branch',
      isBusy: true,
      buttonLabel: this.$t('Save'),
      modalTitle: this.$t('Create Overage Account'),
      isReadonly: false,
      required,
      isRequired: false,
      isLoading: false,
      sortDesc: false,
      showSpinner: false,
      toAmount,
      tableHeaders: [
        { key: 'SN', label: 'S/N' },
        { key: 'accountName', sortable: true, label: 'Account Name' },
        { key: 'accountNumber', sortable: true, label: 'Account Number' },
        { key: 'branchName', sortable: true, label: 'Branch' },
        { key: 'actions', label: 'Actions' },
      ],
      accountTypeOptions: [],
      overageAccountFormData: {
        branchCode: '',
        accountNumber: '',
      },
      cashVolumeOptions: [
        { label: 'Unrequired', value: 1 },
        { label: 'Required', value: 2 },
      ],
      overageAccountList: [],
      tellerNameOptions: [],
      filter: {
        branchID: '',
        accountNumber: '',
      },
    }
  },
  computed: {
    ...mapGetters({
      branchOptions: 'operations/branchOptions',
    }),
    translatedTableHeaders() {
      return this.tableHeaders.map(header => ({ ...header, label: header.label ? this.$t(header.label) : header.key }))
    },
  },
  mounted() {
    this.getAccounts()
    this.fetchOverageAccounts()
  },
  methods: {
    ...mapActions({
      getOverageAccountList: 'operations/getOverageAccountList',
      createOverageAccount: 'operations/createOverageAccountAction',
      updateOverageAccount: 'operations/updateOverageAccountAction',
      getAccountCategoryByCode: 'account/getAccountCategoryByCode',
      deleteOverageAccount: 'operations/deleteBusinessAutomation',
    }),

    onSelectOverageAccount(items, activity, val) {
      this.isReadonly = val === 'edit' ? this.isReadonly = false : this.isReadonly = true
      this.modalTitle = val === 'edit' ? this.$t('Edit Overage Account') : this.$t('Overage Account')
      this.overageAccountFormData = { ...items }
      this.buttonLabel = this.$t('Update')
      this.$root.$emit('bv::show::modal', 'modal-lg')
    },

    async overageAccountAction() {
      try {
        this.isLoading = true
        const response = this.buttonLabel === 'Save' ? await this.createOverageAccount(this.overageAccountFormData) : await this.updateOverageAccount(this.overageAccountFormData)
        const { status } = response
        if (status === 200) {
          this.fetchOverageAccounts()
          this.hideModal('modal-lg')
          this.alertSuccess({ text: this.$t('Overage Account created successfully') })
        }
      } catch (error) {
        this.handleError(error)
      } finally {
        this.isLoading = false
      }
    },
    handleSubmit(bvModalEvt) {
      bvModalEvt.preventDefault()
      this.$refs.overageAccountRules.validate().then(success => {
        if (success) {
          const areYouSure = this.$t('Are you sure you want to')
          const thisOverage = this.$t('this Overage Account?')
          this.confirm(this.overageAccountAction, { text: `${areYouSure} ${this.buttonLabel} ${thisOverage}` })
        }
      })
    },

    async fetchOverageAccounts() {
      try {
        this.isBusy = true
        const response = await this.getOverageAccountList({
          search: this.filter,
          page: this.currentPage,
          limit: this.perPage,
        })
        const { data, status } = response
        if (status === 200 && data.data) {
          this.isBusy = false
          this.overageAccountList = data.data
          this.totalCount = data.recordCount
        } else {
          this.overageAccountList = []
          this.totalCount = 0
        }
      } catch (error) {
        this.alertError({ text: error })
      } finally {
        this.isBusy = false
      }
    },
    async getAccounts() {
      try {
        const response = await this.getAccountCategoryByCode(20419000)
        const { data, status } = response
        if (status === 200 && data.data) {
          this.accountTypeOptions = data.data.filter(item => item.status === 'Active').map(item => ({
            label: item.name,
            value: item.code.toString(),
          }))
        }
      } catch (error) {
        this.alertError({ text: error })
      }
    },
    async onDeleteOverageAccount(item) {
      const deleteOverageAccount = async () => {
        try {
          this.showSpinner = true
          const response = await this.deleteOverageAccount({ routeName: 'OverageAccount', id: item.id })
          const { status } = response
          if (status === 200) {
            this.alertSuccess({ text: this.$t('Deleted successfully') })
          }
        } catch (error) {
          this.alertError({ text: error })
        } finally {
          this.showSpinner = false
        }
      }
      this.confirm(deleteOverageAccount, { text: this.$t('Are you sure you want to delete this Overage Account?') })
    },
    resetModal() {
      this.overageAccountFormData = { ...null }
      this.isLoading = false
    },
    resetFilters() {
      this.filter = { ...null }
    },
    resetFilter() {
      this.filter = { ...null }
      this.fetchOverageAccounts()
    },
  },
}
</script>

<style lang="scss">
@import '@core/scss/vue/libs/vue-select.scss';
</style>
